// 2026-06-23 (ADR-015 D11) — translates an `ImportBatch.Source` string into a
// treasurer-first label for the import-history list on the reframed Sources
// front door. Sibling of `moneyMovementLabels` / `aliasResolutionLabels`: a
// pure, React-free map so it can be reused and snapshot-tested cheaply. Per
// `feedback_treasurer_first_copy.md` the engineer-facing source identifier
// (`CompiledDonations`, `NumeroContributions`, …) never renders verbatim — this
// is the single Source→label map; do not add a second hardcoded list.
//
// The keys are the persisted `ImportSources` strings. The
// two compiled sources read off-platform per the locked copy; processor /
// payout sources collapse to the platform name (a treasurer thinks "Numero",
// not "NumeroContributions"); the catch-alls read as plain words.

import { FINANCE_IMPORT_SOURCES } from '../generated/constants.gen';

type ImportSource = (typeof FINANCE_IMPORT_SOURCES)[keyof typeof FINANCE_IMPORT_SOURCES];

/**
 * Per-source treasurer-first label, keyed on the API's generated source list so a
 * new source without a label fails type-check. Exported so a test can pin the literals.
 */
export const IMPORT_SOURCE_LABELS: Readonly<Record<ImportSource, string>> = {
  // Off-platform compiled sheets (the Tier-2 long tail) — never a Source platform.
  CompiledDonations: 'Off-platform donations',
  CompiledExpenses: 'Expenses',
  // Known Source platforms — both the contributions and payout halves collapse
  // to the bare platform name the treasurer recognizes.
  NumeroContributions: 'Numero',
  NumeroPayouts: 'Numero',
  WinRedContributions: 'WinRed',
  WinRedPayout: 'WinRed',
  AnedotContributions: 'Anedot',
  AnedotPayout: 'Anedot',
  StripePayout: 'Stripe',
  ActBluePreserved: 'ActBlue',
  ActBlueContributions: 'ActBlue',
  // Bank statement upload.
  BankFile: 'Bank statement',
  PlaidSync: 'Bank sync',
  // The prior-filing import that brings in reports the campaign filed before it joined.
  PriorFiledBackfill: 'Previously filed reports',
  // Peer-to-peer apps (Venmo / Zelle / Cash App) + manual + not-yet-classified.
  ManualP2P: 'Peer-to-peer app',
  Manual: 'Manual entry',
  Unattributed: 'Not yet classified',
};

/**
 * Resolves the treasurer-first display label for an `ImportBatch.Source`. Takes
 * the raw wire string and passes any unmapped value through unchanged — never
 * empty, never throws — so a future source added server-side degrades to its
 * own name rather than crashing the import-history route.
 */
export function importSourceLabel(source: string): string {
  return (IMPORT_SOURCE_LABELS as Readonly<Record<string, string>>)[source] ?? source;
}
